import Vue from 'vue';
import tokenUtils from '../../plugins/tokenUtils';
import authBff, { isAuthBffConfigured } from '../../plugins/authBff';
import {
  hostModeFromDomain,
  resolveHostMode,
  shouldCollapseDedicatedNavigation,
} from '../../plugins/hostMode';
import constants from '../../plugins/constants';
import {
  getLocalLoginErrorMessage,
  isDiscoverStatusNotFound,
} from '../../views/Auth/utils/authLoginUtils';

const { AUTH, AUTH_BFF, API_URLS } = constants;

function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/**
 * Probe Auth BFF discover without changing login step.
 * `not_found` | `found` | `skipped` (BFF not configured) | `indeterminate` (transport/server failure).
 */
async function probeDiscoverEmailStatus(email) {
  if (!isAuthBffConfigured()) return 'skipped';
  try {
    const { data } = await authBff.post(AUTH_BFF.DISCOVER, {
      email: normalizeEmail(email),
    });
    if (isDiscoverStatusNotFound(data?.status)) return 'not_found';
    return 'found';
  } catch (err) {
    console.info({
      message: 'SSO discover outcome',
      statusCode: err?.response?.status,
      meta: { status: 'indeterminate', probe: true },
    });
    return 'indeterminate';
  }
}

function readJwtHeader(response) {
  const headers = response?.headers || {};
  return headers['x-jwt-token'] || headers['X-JWT-Token'] || '';
}

function pickUserPayload(data) {
  if (!data) return {};
  if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
    return data.data;
  }
  if (Array.isArray(data.data)) return data.data[0] || {};
  return data;
}

async function postPasswordReset(url, body) {
  try {
    const response = body
      ? await Vue.axios.post(url, body)
      : await Vue.axios.post(url);
    const data = response?.data || {};
    if (data.status === 'success') {
      return { success: true, message: data.message || '', data };
    }
    return {
      success: false,
      message: data.message || AUTH.RESET_GENERIC_ERROR,
      data,
    };
  } catch (error) {
    return {
      success: false,
      message: error?.message || AUTH.RESET_GENERIC_ERROR,
      error,
    };
  }
}

function waitForPaint() {
  return Vue.nextTick().then(
    () =>
      new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        } else {
          resolve();
        }
      }),
  );
}

export default {
  namespaced: true,
  state: () => ({
    authMode: null,
    loginHostname: '',
    boundClient: null,
    hostMode: AUTH.HOST_MODES.UNKNOWN,
    loginStep: AUTH.LOGIN_STEPS.DISCOVER,
    discoverEmail: '',
    discoverTenants: [],
    loginOptions: null,
    selectedDiscoverClient: null,
    ssoInProgress: false,
    loading: false,
    errorMessage: '',
  }),
  getters: {
    isJwtMode: (state) => state.authMode === AUTH.AUTH_MODES.JWT,
    isAppHost: (state) => state.hostMode === AUTH.HOST_MODES.APP_HOST,
    isDedicatedHost: (state) => state.hostMode === AUTH.HOST_MODES.DEDICATED,
    selectedClient: (state) =>
      state.selectedDiscoverClient ||
      state.boundClient ||
      (state.loginOptions && state.loginOptions.client) ||
      null,
    authPolicy: (state) => state.loginOptions?.authPolicy || '',
    localAllowed: (state) => Boolean(state.loginOptions?.localAllowed),
    idps: (state) => {
      const policy = state.loginOptions?.authPolicy;
      if (policy === AUTH.AUTH_POLICIES.LOCAL_ONLY) return [];
      return Array.isArray(state.loginOptions?.idps) ? state.loginOptions.idps : [];
    },
  },
  mutations: {
    setHostMode(state, mode) {
      state.hostMode = mode || AUTH.HOST_MODES.UNKNOWN;
    },
    setLoginStep(state, step) {
      state.loginStep = step || AUTH.LOGIN_STEPS.DISCOVER;
    },
    setDiscoverEmail(state, email) {
      state.discoverEmail = normalizeEmail(email);
    },
    setDiscoverTenants(state, tenants) {
      state.discoverTenants = Array.isArray(tenants) ? tenants : [];
    },
    setLoginOptions(state, options) {
      state.loginOptions = options || null;
    },
    setSelectedDiscoverClient(state, clientId) {
      state.selectedDiscoverClient = clientId || null;
    },
    setBoundClient(state, clientId) {
      state.boundClient = clientId || null;
    },
    setLoginHostname(state, hostname) {
      state.loginHostname = hostname || '';
    },
    setAuthMode(state, mode) {
      state.authMode = mode || null;
    },
    setSsoInProgress(state, value) {
      state.ssoInProgress = !!value;
    },
    setLoading(state, value) {
      state.loading = !!value;
    },
    setErrorMessage(state, message) {
      state.errorMessage = message || '';
    },
    clearTransient(state) {
      state.discoverEmail = '';
      state.discoverTenants = [];
      state.loginOptions = null;
      state.selectedDiscoverClient = null;
      state.ssoInProgress = false;
      state.loading = false;
      state.errorMessage = '';
      state.loginStep =
        state.hostMode === AUTH.HOST_MODES.DEDICATED
          ? AUTH.LOGIN_STEPS.CREDENTIALS
          : AUTH.LOGIN_STEPS.DISCOVER;
    },
    clearSessionState(state) {
      state.authMode = null;
      state.loginHostname = '';
      state.boundClient = null;
      state.discoverEmail = '';
      state.discoverTenants = [];
      state.loginOptions = null;
      state.selectedDiscoverClient = null;
      state.ssoInProgress = false;
      state.loading = false;
      state.errorMessage = '';
      state.loginStep =
        state.hostMode === AUTH.HOST_MODES.DEDICATED
          ? AUTH.LOGIN_STEPS.CREDENTIALS
          : AUTH.LOGIN_STEPS.DISCOVER;
    },
  },
  actions: {
    initHostMode({ commit }) {
      const mode = resolveHostMode();
      const loginStep =
        mode === AUTH.HOST_MODES.DEDICATED
          ? AUTH.LOGIN_STEPS.CREDENTIALS
          : AUTH.LOGIN_STEPS.DISCOVER;
      commit('setHostMode', mode);
      commit('setLoginStep', loginStep);
      return mode;
    },

    setAuthFromResponse({ commit }, response) {
      const jwtToken = readJwtHeader(response);
      if (jwtToken) {
        tokenUtils.storeSecureToken(jwtToken);
        commit('setAuthMode', AUTH.AUTH_MODES.JWT);
        return AUTH.AUTH_MODES.JWT;
      }
      const bodyMode = response?.data?.authMode || response?.data?.data?.authMode;
      if (bodyMode === AUTH.AUTH_MODES.JWT || bodyMode === AUTH.AUTH_MODES.SESSION) {
        commit('setAuthMode', bodyMode);
        return bodyMode;
      }
      // No access token → session mode (cookie-only)
      if (!tokenUtils.getSecureToken()) {
        commit('setAuthMode', AUTH.AUTH_MODES.SESSION);
        return AUTH.AUTH_MODES.SESSION;
      }
      commit('setAuthMode', AUTH.AUTH_MODES.JWT);
      return AUTH.AUTH_MODES.JWT;
    },

    applyAuthSuccess({ commit, dispatch, rootState }, { response, clientId, loginHostname } = {}) {
      const payload = pickUserPayload(response?.data);
      const boundClient =
        clientId ||
        payload.client ||
        payload.boundClient ||
        (payload.clients && payload.clients[0] && (payload.clients[0]._id || payload.clients[0])) ||
        null;
      const host =
        loginHostname ||
        payload.loginHostname ||
        (typeof window !== 'undefined' ? window.location.hostname : '');

      dispatch('setAuthFromResponse', response);
      commit('setBoundClient', boundClient);
      commit('setLoginHostname', host);
      const resolvedMode = hostModeFromDomain(payload);
      if (resolvedMode !== AUTH.HOST_MODES.UNKNOWN) {
        commit('setHostMode', resolvedMode);
      }
      if (payload && Object.keys(payload).length) {
        commit('setUser', payload, { root: true });
      }
      if (boundClient) {
        // Do not clobber a soft-switched org (localStorage Entity / getdetails) on refresh
        if (!rootState.client) {
          commit('setClient', boundClient, { root: true });
        }
      }
      commit('changeLogin', true, { root: true });
      return payload;
    },

    clearSession({ commit }) {
      try {
        tokenUtils.removeSecureToken();
      } catch (_) { /* storage unavailable */ }
      commit('clearSessionState');
      commit('changeLogin', false, { root: true });
      commit('resetUser', null, { root: true });
    },

    async discover({ commit, dispatch }, email) {
      if (!isAuthBffConfigured()) {
        console.info({ message: 'SSO discover outcome', meta: { status: 'not_found' } });
        commit('setErrorMessage', AUTH.GENERIC_DISCOVER_ERROR);
        return { status: 'not_found' };
      }
      const normalized = normalizeEmail(email);
      commit('setLoading', true);
      commit('setErrorMessage', '');
      commit('setDiscoverEmail', normalized);
      try {
        const { data } = await authBff.post(AUTH_BFF.DISCOVER, { email: normalized });
        const status = data?.status || 'not_found';
        const tenants = Array.isArray(data?.tenants) ? data.tenants : [];
        commit('setDiscoverTenants', tenants);

        // Single domain: enter it directly even if it is the app host (no per-client picker).
        if (tenants.length === 1 && status === 'choose') {
          const only = tenants[0];
          const outcome =
            only.hostingMode === 'DEDICATED_SUBDOMAIN' ? 'redirect' : 'login_here';
          console.info({ message: 'SSO discover outcome', meta: { status: outcome } });
          await dispatch('selectDiscoverTenant', only);
          return { ...data, status: outcome };
        }

        if (status === 'redirect') {
          const target = tenants[0];
          // L1 / loopback: never leave localhost for a dedicated public hostname.
          if (shouldCollapseDedicatedNavigation() && target?.client) {
            console.info({ message: 'SSO discover outcome', meta: { status: 'login_here' } });
            commit('setSelectedDiscoverClient', target.client);
            commit('setLoginStep', AUTH.LOGIN_STEPS.CREDENTIALS);
            await dispatch('loadLoginOptions', { client: target.client });
            return { ...data, status: 'login_here' };
          }
          if (target?.hostname && !shouldCollapseDedicatedNavigation()) {
            console.info({ message: 'SSO discover outcome', meta: { status: 'redirect' } });
            const qs = new URLSearchParams({ email: normalized });
            window.location.assign(`https://${target.hostname}/login?${qs.toString()}`);
            return data;
          }
          console.info({ message: 'SSO discover outcome', meta: { status: 'redirect' } });
          commit('setErrorMessage', AUTH.GENERIC_DISCOVER_ERROR);
          return data;
        }

        if (status === 'login_here') {
          const clientId = tenants[0]?.client || null;
          console.info({ message: 'SSO discover outcome', meta: { status: 'login_here' } });
          commit('setSelectedDiscoverClient', clientId);
          commit('setLoginStep', AUTH.LOGIN_STEPS.CREDENTIALS);
          await dispatch('loadLoginOptions', { client: clientId || undefined });
          return data;
        }

        if (status === 'choose') {
          console.info({ message: 'SSO discover outcome', meta: { status: 'choose' } });
          commit('setLoginStep', AUTH.LOGIN_STEPS.CHOOSE);
          return data;
        }

        console.info({
          message: 'SSO discover outcome',
          meta: { status: status || 'not_found' },
        });
        commit('setErrorMessage', AUTH.GENERIC_DISCOVER_ERROR);
        commit('setLoginStep', AUTH.LOGIN_STEPS.DISCOVER);
        return data || { status: 'not_found' };
      } catch (err) {
        console.info({
          message: 'SSO discover outcome',
          statusCode: err?.response?.status,
          meta: { status: 'not_found' },
        });
        commit('setErrorMessage', AUTH.GENERIC_DISCOVER_ERROR);
        return { status: 'not_found', error: err };
      } finally {
        commit('setLoading', false);
      }
    },

    async selectDiscoverTenant({ commit, dispatch, state }, tenant) {
      if (!tenant) {
        return;
      }
      commit('setErrorMessage', '');
      const hostingMode = tenant.hostingMode || '';
      const email = state.discoverEmail || '';

      if (
        hostingMode === 'DEDICATED_SUBDOMAIN' &&
        tenant.hostname &&
        !shouldCollapseDedicatedNavigation()
      ) {
        const qs = new URLSearchParams();
        if (email) qs.set('email', email);
        window.location.assign(`https://${tenant.hostname}/login?${qs.toString()}`);
        return;
      }

      commit('setSelectedDiscoverClient', tenant.client);
      commit('setLoginStep', AUTH.LOGIN_STEPS.CREDENTIALS);
      await dispatch('loadLoginOptions', { client: tenant.client });
    },

    async loadLoginOptions({ commit, state, getters }, { client } = {}) {
      if (!isAuthBffConfigured()) {
        commit('setErrorMessage', AUTH.SITE_UNAVAILABLE);
        return null;
      }
      const clientId = client || getters.selectedClient || state.selectedDiscoverClient;
      commit('setLoading', true);
      commit('setErrorMessage', '');
      try {
        const params = {};
        if (clientId) params.client = clientId;
        const { data } = await authBff.get(AUTH_BFF.LOGIN_OPTIONS, { params });
        commit('setLoginOptions', data || null);
        const resolvedMode = hostModeFromDomain(data || {});
        if (resolvedMode !== AUTH.HOST_MODES.UNKNOWN) {
          commit('setHostMode', resolvedMode);
        }
        if (data?.client) {
          commit('setSelectedDiscoverClient', data.client);
        }
        return data;
      } catch (err) {
        commit('setErrorMessage', err?.message || AUTH.SITE_UNAVAILABLE);
        return null;
      } finally {
        commit('setLoading', false);
      }
    },

    async startSso({ commit, getters, state }, { connectionId, returnPath } = {}) {
      if (!isAuthBffConfigured()) {
        commit('setErrorMessage', AUTH.SITE_UNAVAILABLE);
        return null;
      }
      const client = getters.selectedClient;
      if (!connectionId) {
        commit('setErrorMessage', 'Unable to start sign-in.');
        return null;
      }
      commit('setSsoInProgress', true);
      commit('setLoading', true);
      commit('setErrorMessage', '');
      // Let SSO handoff paint before the BFF call / IdP navigation.
      await waitForPaint();
      try {
        const body = {
          connectionId,
          returnPath: returnPath || '/',
        };
        if (client) body.client = client;
        const response = await authBff.post(AUTH_BFF.SSO_START, body);
        const authorizeUrl = response?.data?.authorizeUrl;
        if (authorizeUrl) {
          let authorizeHost = '';
          try {
            authorizeHost = new URL(authorizeUrl).host;
          } catch (_) { /* ignore invalid URL */ }
          console.info({
            message: 'SSO start',
            meta: { authorizeHost: authorizeHost || undefined },
          });
          if (!state.ssoInProgress) {
            return null;
          }
          window.location.assign(authorizeUrl);
          return response.data;
        }
        // Some BFF implementations may 302; axios follows redirects by default for XHR
        // and returns final response — treat missing URL as error.
        console.error({
          message: 'SSO start',
          statusCode: response?.status,
          meta: { success: false },
        });
        commit('setErrorMessage', 'Unable to start sign-in.');
        commit('setSsoInProgress', false);
        return null;
      } catch (err) {
        if (!state.ssoInProgress) {
          return null;
        }
        const redirectUrl =
          err?.response?.headers?.location ||
          err?.response?.data?.authorizeUrl;
        if (redirectUrl) {
          let redirectHost = '';
          try {
            redirectHost = new URL(redirectUrl).host;
          } catch (_) { /* ignore invalid URL */ }
          console.info({
            message: 'SSO start',
            meta: { authorizeHost: redirectHost || undefined },
          });
          if (!state.ssoInProgress) {
            return null;
          }
          window.location.assign(redirectUrl);
          return { authorizeUrl: redirectUrl };
        }
        console.error({
          message: 'SSO start',
          statusCode: err?.response?.status,
          meta: { success: false },
        });
        commit('setErrorMessage', err?.message || 'Unable to start sign-in.');
        commit('setSsoInProgress', false);
        return null;
      } finally {
        commit('setLoading', false);
      }
    },

    async completeSso({ commit, dispatch }, { code, state: oauthState } = {}) {
      if (!isAuthBffConfigured()) {
        console.error({
          message: 'SSO exchange complete',
          meta: { success: false },
        });
        commit('setErrorMessage', AUTH.SITE_UNAVAILABLE);
        throw new Error(AUTH.SITE_UNAVAILABLE);
      }
      commit('setSsoInProgress', true);
      commit('setErrorMessage', '');
      try {
        const exchange = await authBff.post(AUTH_BFF.SSO_EXCHANGE, {
          code,
          state: oauthState,
        });
        const handoff = exchange?.data?.handoff;
        if (!handoff) {
          throw new Error('Sign-in could not be completed.');
        }
        const complete = await Vue.axios.post(API_URLS.AUTH_SSO_COMPLETE, { handoff });
        if (complete?.data?.status && complete.data.status !== 'success') {
          throw new Error(complete.data.message || 'Sign-in could not be completed.');
        }
        await dispatch('applyAuthSuccess', {
          response: complete,
          clientId: complete?.data?.client || complete?.data?.data?.client,
          loginHostname: complete?.data?.loginHostname || complete?.data?.data?.loginHostname,
        });
        console.info({
          message: 'SSO exchange complete',
          meta: { success: true },
        });
        return complete.data;
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          'Sign-in could not be completed.';
        console.error({
          message: 'SSO exchange complete',
          statusCode: err?.response?.status,
          meta: { success: false },
        });
        commit('setErrorMessage', message);
        throw err;
      } finally {
        commit('setSsoInProgress', false);
      }
    },

    async localLogin({ commit, dispatch, getters, state }, { email, password } = {}) {
      const client = getters.selectedClient;
      const username = normalizeEmail(email || state.discoverEmail);
      const knownEmail = normalizeEmail(state.discoverEmail);
      const emailWasDiscovered = Boolean(knownEmail && username === knownEmail);
      commit('setLoading', true);
      commit('setErrorMessage', '');
      try {
        const probe = emailWasDiscovered
          ? 'found'
          : await probeDiscoverEmailStatus(username);
        if (probe === 'not_found') {
          commit('setErrorMessage', getLocalLoginErrorMessage(true));
          return null;
        }
        const body = { username, password };
        if (client) body.client = client;
        const response = await Vue.axios.post(API_URLS.AUTH_LOCAL, body);
        if (response?.data?.status !== 'success') {
          commit('setErrorMessage', getLocalLoginErrorMessage(false));
          return null;
        }
        await dispatch('applyAuthSuccess', {
          response,
          clientId: client || response?.data?.client || response?.data?.data?.client,
        });
        return response.data;
      } catch (err) {
        commit('setErrorMessage', getLocalLoginErrorMessage(false));
        return null;
      } finally {
        commit('setLoading', false);
      }
    },

    async forgotPassword(_, username) {
      const email = String(username || '').trim();
      console.log('[AuthStore]: forgotPassword', { username: email });
      const result = await postPasswordReset(
        `${API_URLS.AUTH_FORGOT_PASSWORD}/${email}`,
      );
      if (!result.success) {
        console.error('[AuthStore]: forgotPassword error', result.error || result);
      }
      return result;
    },

    async resendPassword(_, username) {
      const email = String(username || '').trim();
      console.log('[AuthStore]: resendPassword', { username: email });
      const result = await postPasswordReset(API_URLS.AUTH_RESEND_PASSWORD, {
        username: email,
      });
      if (!result.success) {
        console.error('[AuthStore]: resendPassword error', result.error || result);
      }
      return result;
    },

    async checkSetPassword(_, email) {
      const username = String(email || '').trim();
      console.log('[AuthStore]: checkSetPassword', { username });
      const result = await postPasswordReset(
        `${API_URLS.AUTH_CHECK_SET_PASSWORD}/${username}`,
      );
      if (!result.success) {
        console.error('[AuthStore]: checkSetPassword error', result.error || result);
      }
      return result;
    },

    async setPassword(_, form) {
      console.log('[AuthStore]: setPassword');
      const result = await postPasswordReset(API_URLS.AUTH_SET_PASSWORD, {
        data: form,
      });
      if (!result.success) {
        console.error('[AuthStore]: setPassword error', result.error || result);
      }
      return result;
    },
  },
};
